import createReducer from "./createReducer";
import moment from "moment";
import { downloadBlob } from "./Download";

import { addNotification } from "./Notifications";
import { addFiltering } from "./common/Filtering";
import { addColumnFilters } from "./common/ColumnFilters";
import { SUPPRESS_SUCCESS_GROWL } from "utils/constants";
import { canDeleteConcept } from 'utils/CreativeActions';

import {
	CREATIVES_ACTIONS,
	CREATIVE_DETAILS_ACTIONS,
	CREATIVE_TAXONOMY_ACTIONS,
	CREATIVE_VAST_ACTIONS
} from "./actions/Creatives";

import { clearLevel } from "reducers/Selected";
import { CONCEPTS_ACTIONS } from "./actions/Concepts";
import { LABELS_ACTIONS } from "./Labels";
import * as ConceptUpdates from "./creatives/ConceptUpdates";
import * as VASTUpdates from "./creatives/VAST";
import { buildNoFileMessage, escapeUnderscoreForAPI, mergeItem, noop } from "utils/common";

export function setActiveCreative(creativeId) {
	return ((dispatch) => {
		dispatch({
			type: CREATIVES_ACTIONS.SET_ACTIVE_CREATIVE,
			payload: { creativeId },
		});
	});
}

export function clearActiveCreative() {
	return ((dispatch) => {
		dispatch({
			type: CREATIVES_ACTIONS.CLEAR_ACTIVE_CREATIVE,
		});
	});
}

// [CREATIVES_ACTIONS].[LOAD_CREATIVES_AJAX] Action Creators //
export function loadCreatives(libraryId, cancelToken) {
	return (dispatch, getState) => {
		let state = getState();
		const { filterInfo = {} } = state.creatives || {};
		const params = { ...filterInfo };

		if (params.searchTerm) {
			params.searchTerm = escapeUnderscoreForAPI(filterInfo.searchTerm);
		}

		return dispatch({
			type: CREATIVES_ACTIONS.LOAD_CREATIVES_AJAX,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives`,
				params,
				success: CREATIVES_ACTIONS.LOAD_CREATIVES_SUCCESS,
				error: CREATIVES_ACTIONS.LOAD_CREATIVES_ERROR,
				cancel: CREATIVES_ACTIONS.LOAD_CREATIVES_CANCEL,
				cancelToken,
			},
		});
	};
}

export function fetchCreativesById(libraryId, creativeIds = []){
	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.FETCH_CREATIVES_BY_ID,
			AJAX: {
				url: `ui/creative-libraries/${libraryId}/creatives`,
				params: {
					advancedFilter: `in(id,${creativeIds.join(",")})`,
				},
				success: CREATIVES_ACTIONS.FETCH_CREATIVES_BY_ID_SUCCESS,
				error: CREATIVES_ACTIONS.FETCH_CREATIVES_BY_ID_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		});
	};
}

export function clearCreatives() {
	return (dispatch) => {
		dispatch({
			type: CREATIVES_ACTIONS.RESET_CREATIVES_LIST,
		});
		return Promise.resolve({ success: true });
	};
}

export function fetchFilenameConflicts(libraryId, filenames){
	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.FETCH_CREATIVE_FILENAME_CONFLICTS_AJAX,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives`,
				params: {
					advancedFilter: `in(filename,${filenames.join(",")})`,
					rpp: filenames.length,
				},
				success: CREATIVES_ACTIONS.FETCH_CREATIVE_FILENAME_CONFLICTS_SUCCESS,
				error: CREATIVES_ACTIONS.FETCH_CREATIVE_FILENAME_CONFLICTS_ERROR,
			},
		}).then((response) => {
			if(response?.success){
				return response.payload?.data?.items || [];
			}
			throw { description: "There was a problem verifying name conflicts.  Please try again." };
		});
	};
}

export function uploadCreative(libraryId, formData, extraArgs){
	/*
		`formData` should be a FormData object, with a single file, keyed as: `file`.

		If `assetConflictResolution` is set, it will be given as a param to the endpoint,
		to determine what to do with images within a .zip Creative that already exist within the library.
	*/
	const {
		creativeId, // If creativeId is provided, we use the overwrite endpoint, instead of import
		assetConflictResolution, // should be one of: ["overwriteExistingImages", "useExistingImages"]
		onUploadProgress = noop,
		meta = {},
	} = extraArgs || {};

	// `ui/` is intentionally omitted on the import URL because the ui URL auto approves images, which we don't want.
	let url = creativeId ? `ui/creative-libraries/${libraryId}/creatives/overwrite/${creativeId}` : `creative-libraries/${libraryId}/creatives/import`;
	let AJAX = {
		url,
		method: "post",
		headers: { "Content-Type": "multipart/form-data" },
		data: formData,
		success: CREATIVES_ACTIONS.UPLOAD_CREATIVE_SUCCESS,
		error: CREATIVES_ACTIONS.UPLOAD_CREATIVE_ERROR,
		onUploadProgress,
	};

	if(assetConflictResolution){
		AJAX.params = { [assetConflictResolution]: true };
	}

	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.UPLOAD_CREATIVE_AJAX,
			meta,
			AJAX,
		});
	};
}

export function deleteCreatives(libraryId, creativeIds) {
	return ((dispatch) => {
		dispatch(clearLevel('creatives')); //app crashes if non-existent creatives are selected
		return (dispatch({
			type: CREATIVES_ACTIONS.DELETE_CREATIVES_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives`,
				method: 'delete',
				data: { creatives: creativeIds.map(id => ({ id })) }, // [{ id: 1 }, { id: 2 }]
				success: CREATIVES_ACTIONS.DELETE_CREATIVES_SUCCESS,
				error: CREATIVES_ACTIONS.DELETE_CREATIVES_ERROR,
			},
		}));
	});
}

export function renameCreative(values) {
	const { creativeId, libraryId, newName } = values;
	return ((dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.RENAME_CREATIVE_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/${creativeId}`,
				method: 'put',
				data: { name: newName },
				success: CREATIVES_ACTIONS.RENAME_CREATIVE_SUCCESS,
				error: CREATIVES_ACTIONS.RENAME_CREATIVE_ERROR,
			},
		});
	});
}

export function renameCreativeFilename(values) {
	const { creativeId, libraryId, newFilename } = values;
	return ((dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.RENAME_CREATIVE_FILENAME_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/${creativeId}`,
				method: 'put',
				data: { filename: newFilename },
				success: CREATIVES_ACTIONS.RENAME_CREATIVE_FILENAME_SUCCESS,
				error: CREATIVES_ACTIONS.RENAME_CREATIVE_FILENAME_ERROR,
			},
		});
	});
}

export function bulkStatusChange(values) {
	const { status, libraryId, creativeIds, reason } = values;

	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.BULK_STATUS_CHANGE_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creative-status/${status}`,
				method: 'post',
				data: {
					creativeIdList: creativeIds,
					reason,
				},
				success: CREATIVES_ACTIONS.BULK_STATUS_CHANGE_SUCCESS,
				error: CREATIVES_ACTIONS.BULK_STATUS_CHANGE_ERROR,
			},
		});
	};
}

export function statusChange(values) {
	const { status, libraryId, creativeId, reason } = values;

	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.STATUS_CHANGE_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creative-status/${status}`,
				method: 'post',
				data: {
					creativeIdList: [creativeId],
					reason,
				},
				success: CREATIVES_ACTIONS.STATUS_CHANGE_SUCCESS,
				error: CREATIVES_ACTIONS.STATUS_CHANGE_ERROR,
			},
		});
	};
}

export function submitCreativesToQA(values) {
	const {
		libraryId,
		creativeIds,
		mediaBuyerEmail,
		sendMediaBuyerEmail,
		primaryEmail,
		sendPrimaryEmail,
		otherRecipients,
		notes,
	} = values;

	let  recipients = [];
	if (sendMediaBuyerEmail) {
		recipients.push(mediaBuyerEmail);
	}
	if (sendPrimaryEmail) {
		recipients.push(primaryEmail);
	}
	recipients = [].concat(recipients, otherRecipients.filter(recipient => !!recipient));

	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.BULK_STATUS_CHANGE_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creative-status/submit`,
				method: 'post',
				data: {
					creativeIdList: creativeIds,
					notes,
					recipients,
				},
				success: CREATIVES_ACTIONS.BULK_STATUS_CHANGE_SUCCESS,
				error: CREATIVES_ACTIONS.BULK_STATUS_CHANGE_ERROR,
			},
		});
	};
}

export function sendForAutoClassification(libraryId, creativeIds = []){
	const creatives = creativeIds.map((id) => {
		return { id };
	});

	return (dispatch) => {
		return dispatch({
			type: CREATIVE_TAXONOMY_ACTIONS.CLASSIFY_CREATIVES_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/classification`,
				method: "post",
				data: {
					creatives,
				},
				success: CREATIVE_TAXONOMY_ACTIONS.CLASSIFY_CREATIVES_SUCCESS,
				error: CREATIVE_TAXONOMY_ACTIONS.CLASSIFY_CREATIVES_ERROR,
			},
			meta: {
				buildMessage(response, success){
					if(success){
						return {
							type: "success",
							id: new Date().getTime(),
							messageHeader: "Success",
							message: "Creatives sent for auto-classification",
						};
					} else {
						return {
							type: "error",
							id: new Date().getTime(),
							messageHeader: "Error",
							message: "Error sending Creatives for auto-classification",
						};
					}
				},
			},
		});
	};
}

export function importCreativeLabels(data, libraryId) {
	return (dispatch) => {
		return dispatch({
			type: CREATIVE_TAXONOMY_ACTIONS.IMPORT_CREATIVE_LABELS_AJAX,
			AJAX: {
				method: "post",
				url: `creative-libraries/${libraryId}/creatives/labels/import/async`,
				data,
				headers: { "Content-Type": "multipart/form-data" },
				success: CREATIVE_TAXONOMY_ACTIONS.IMPORT_CREATIVE_LABELS_SUCCESS,
				error: CREATIVE_TAXONOMY_ACTIONS.IMPORT_CREATIVE_LABELS_ERROR,
			},
		});
	};
}

/**
 * Socket Updates
 ****************/
export function addSocketCreative(library, creative, messageInfo){
	return (dispatch, getState) => {
		let currentLibraryId = getState().libraryProperties.id
			, notify = null
			;

		// If the user has changed libraries, don't try and add it to the list
		if(library?.id  !== currentLibraryId){ return; }

		// TODO: get Growl messages to show up for sockets
		// if(messageInfo && /success|failure/gi.test(messageInfo.status)){
		// 	notify = {
		// 		type:/success/gi.test(messageInfo.status) ? "success" : "error",
		// 		messageHeader:messageInfo.task,
		// 		message:`Video Transcode`
		// 	};
		// }

		return dispatch({
			type:CREATIVES_ACTIONS.ADD_CREATIVE_FROM_SOCKET,
			payload:creative,
			meta:{
				notify,
			},
		});
	};
}

function addSocketCreativeToList(state, action){
	const rpp = state.filterInfo?.rpp;
	const items = mergeItem(state.items, action.payload);
	const newTotalItems = state.totalItems + (items.length - state.items.length);
	let { totalPages } = state;

	// Check if adding items will increase the total pages
	const maxTotalPages = Math.ceil(newTotalItems / rpp);
	if (maxTotalPages > state.totalPages) {
		totalPages = maxTotalPages;
	}

	return {
		...state,
		totalPages,
		totalItems: newTotalItems,
		items: items.slice(0, rpp),
	};
}

function addCreativeToList(state, action){
	// Some endpoints returns a single creative in an array, so we need to extract that
	const { original = {}, data = {} } = action.payload;
	const { isSingleArrayCreative = false, useCreativeName = false } = original;
	const rpp = state.filterInfo?.rpp;
	let newCreative = isSingleArrayCreative ? data.creatives[0] : data;

	// Workaround for creatives that don't include name in the response object, but include creativeName
	if (useCreativeName) {
		newCreative = {
			...newCreative,
			name: newCreative.creativeName,
		};
	}

	const items = mergeItem(state.items, newCreative);
	const newTotalItems = state.totalItems + (items.length - state.items.length);
	let { totalPages } = state;

	// Check if adding items will increase the total pages
	const maxTotalPages = Math.ceil(newTotalItems / rpp);
	if (maxTotalPages > state.totalPages) {
		totalPages = maxTotalPages;
	}

	return {
		...state,
		totalPages,
		totalItems: newTotalItems,
		items: items.slice(0, rpp),
	};
}

export function updateApprovalsFromSocket(creatives, approval) {
	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.UPDATE_APPROVALS_FROM_SOCKET,
			payload: {
				creatives,
				approval,
			},
		});
	};
}

export function updateCreativeVersionCountsFromSocket(creatives) {
	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.UPDATE_CREATIVE_VERIONS_COUNTS_FROM_SOCKET,
			payload: {
				creatives,
			},
		});
	};
}

function updateCreativesApproval(state, action) {
	const oldItems = [...state.items];
	const creativeMap = new Map();
	const { creatives = [], approval } = action.payload;

	oldItems.forEach(creative => creativeMap.set(creative.id, { ...creative }));
	creatives.forEach((creative) => {
		const oldCreative = creativeMap.get(creative.id);
		if (oldCreative) {
			creativeMap.set(oldCreative.id, {
				...oldCreative,
				approval,
			});
		}
	});

	const newItems = [...creativeMap.values()];

	return {
		...state,
		items: newItems,
	};
}

function updateCreativesVersionCounts(state, action) {
	const oldItems = state.items;
	const newItems = [];
	const { creatives = []} = action.payload;
	const creativeVersionSizeMap = new Map();


	// Create map of version size increments by id
	creatives.forEach((creative) => {
		creativeVersionSizeMap.set(creative.id, creative.versions.length);
	});

	// We could have 25 items and not all creatives are in grid so they will not be updated
	// Only visible Items are updated
	oldItems.forEach((creative) => {
		if (creativeVersionSizeMap.has(creative.id)) {
			const newCreative = {
				...creative,
				numberOfVersions: creative.numberOfVersions + creativeVersionSizeMap.get(creative.id),
			};
			newItems.push(newCreative);
		} else {
			newItems.push(creative);
		}
	});


	return {
		...state,
		items: newItems,
	};
}

/**
 * File Downloads
 *****************/
export function downloadCreative(libraryId, creativeIds, fileName){
	return (dispatch) => {
		return dispatch({
			type:CREATIVES_ACTIONS.DOWNLOAD_CREATIVE_AJAX,
			AJAX:{
				method:"post",
				url:`creative-libraries/${libraryId}/creatives/download`,
				responseType: "blob",
				data:{
					creativeIds,
					name:fileName,
				},
				success:CREATIVES_ACTIONS.DOWNLOAD_CREATIVE_SUCCESS,
				error:CREATIVES_ACTIONS.DOWNLOAD_CREATIVE_ERROR,
			},
			meta:{
				buildMessage:buildNoFileMessage,
				tracking:{
					eventCategory:"Action Menu",
					eventAction:"Download",
					eventLabel:"Creative",
					value:creativeIds.length,
				},
			},
		}).then((resp) => {
			return downloadBlob(dispatch, resp, fileName);
		});
	};
}

export function downloadCreativeSummary(libraryId, creativeIds, summaryFileName){
	let now = moment().format("YYYY-MM-DD-HHmm")
		, downloadName = `${summaryFileName}-${now}.csv`
		;

	return (dispatch) => {
		return dispatch({
			type:CREATIVES_ACTIONS.DOWNLOAD_CREATIVE_SUMMARY_AJAX,
			AJAX:{
				method:"post",
				url:`creative-libraries/${libraryId}/creatives/summary`,
				responseType: "blob",
				data:{
					creativeIds,
					name:downloadName,
				},
				success:CREATIVES_ACTIONS.DOWNLOAD_CREATIVE_SUMMARY_SUCCESS,
				error:CREATIVES_ACTIONS.DOWNLOAD_CREATIVE_SUMMARY_ERROR,
			},
			meta:{
				buildMessage:buildNoFileMessage,
				tracking:{
					eventCategory:"Action Menu",
					eventAction:"Download",
					eventLabel:"Summary",
					value:creativeIds.length,
				},
			},
		}).then((resp) => {
			return downloadBlob(dispatch, resp, downloadName);
		});
	};
}

const initialCreativesState = {
	success: true,
	items: [],
	totalPages: 1,
	totalItems: 0,
	isLoaded: false,
	hasErrorLoading: false,
	isUpdating: false,
	isDeleting: false,
	activeCreativeId: null,
};

export const handlers = {
	[CREATIVES_ACTIONS.BULK_STATUS_CHANGE_AJAX]: bulkStatusChangeRequest,
	[CREATIVES_ACTIONS.BULK_STATUS_CHANGE_SUCCESS]: bulkStatusChangeSuccess,
	[CREATIVES_ACTIONS.BULK_STATUS_CHANGE_ERROR]: bulkStatusChangeError,
	[CREATIVES_ACTIONS.RESET_CREATIVES_LIST]				:clearList,
	[CREATIVES_ACTIONS.LOAD_CREATIVES_AJAX]					:isLoadingCreatives,
	[CREATIVES_ACTIONS.LOAD_CREATIVES_SUCCESS]				:initCreatives,
	[CREATIVES_ACTIONS.LOAD_CREATIVES_ERROR]				:initErrorCreatives,
	[CREATIVES_ACTIONS.STATUS_CHANGE_AJAX]					:statusChanged,
	[CREATIVES_ACTIONS.STATUS_CHANGE_SUCCESS]				:successStatusChange,
	[CREATIVES_ACTIONS.STATUS_CHANGE_ERROR]					:errorStatusChange,

	[CREATIVES_ACTIONS.RENAME_CREATIVE_AJAX]: updateCreativeRequest,
	[CREATIVES_ACTIONS.RENAME_CREATIVE_SUCCESS]: updateCreativeSuccess,
	[CREATIVES_ACTIONS.RENAME_CREATIVE_ERROR]: updateCreativeError,
	[CREATIVES_ACTIONS.RENAME_CREATIVE_FILENAME_AJAX]: updateCreativeRequest,
	[CREATIVES_ACTIONS.RENAME_CREATIVE_FILENAME_SUCCESS]: updateCreativeSuccess,
	[CREATIVES_ACTIONS.RENAME_CREATIVE_FILENAME_ERROR]: updateCreativeError,

	[CREATIVES_ACTIONS.DELETE_CREATIVES_AJAX]				:deleteCreativesRequest,
	[CREATIVES_ACTIONS.DELETE_CREATIVES_SUCCESS]		:deleteCreativesSuccess,
	[CREATIVES_ACTIONS.DELETE_CREATIVES_ERROR]			:deleteCreativesError,

	[CREATIVES_ACTIONS.DUPLICATE_CREATIVES_AJAX]: duplicateCreativesRequest,
	[CREATIVES_ACTIONS.DUPLICATE_CREATIVES_SUCCESS]: duplicateCreativesSuccess,
	[CREATIVES_ACTIONS.DUPLICATE_CREATIVES_ERROR]: duplicateCreativesError,

	[CREATIVES_ACTIONS.UPLOAD_CREATIVE_SUCCESS]: addCreativeToList,

	[CREATIVES_ACTIONS.ADD_CREATIVE_FROM_SOCKET]: addSocketCreativeToList,
	[CREATIVES_ACTIONS.UPDATE_APPROVALS_FROM_SOCKET]: updateCreativesApproval,
	[CREATIVES_ACTIONS.UPDATE_CREATIVE_VERIONS_COUNTS_FROM_SOCKET]: updateCreativesVersionCounts,

	[CREATIVES_ACTIONS.SET_ACTIVE_CREATIVE]: setActiveCreativeId,
	[CREATIVES_ACTIONS.CLEAR_ACTIVE_CREATIVE]: clearActiveCreativeId,

	[CREATIVE_VAST_ACTIONS.CREATE_BLANK_IMPOSIUM_CREATIVE_SUCCESS]: addCreativeToList,

	[CREATIVE_DETAILS_ACTIONS.UPDATE_CREATIVE_SUCCESS]: updateRow,
	[CREATIVE_DETAILS_ACTIONS.UPDATE_SOCIAL_CREATIVE_SUCCESS]: updateRow,

	[CREATIVE_DETAILS_ACTIONS.UPDATE_SOCIAL2_CREATIVE_SUCCESS]: updateRow,
	[CREATIVE_DETAILS_ACTIONS.NATIVE_CREATIVE_SUCCESS]: addCreativeToList,
	[CREATIVE_VAST_ACTIONS.UPLOAD_VAST_IMPOSIUM_CREATIVES_SUCCESS]: addCreativeToList,
	[CREATIVE_VAST_ACTIONS.ENCODE_VAST_IMPOSIUM_CREATIVES_SUCCESS]: addCreativeToList,

	[CONCEPTS_ACTIONS.FETCH_CREATIVES_BY_CONCEPT_AJAX]		:getCreativesByConcept,
	[CONCEPTS_ACTIONS.FETCH_CREATIVES_BY_CONCEPT_SUCCESS]	:successCreativesByConcept,
	[CONCEPTS_ACTIONS.FETCH_CREATIVES_BY_CONCEPT_ERROR]		:errorCreativesByConcept,

	[LABELS_ACTIONS.ADD_CREATIVE_LABELS_SUCCESS]: updateLabelCount,
	[LABELS_ACTIONS.DELETE_CREATIVE_LABELS_SUCCESS]: subtractLabels,

	// Spread in the Handler mappings for the Concept Udpates
	...ConceptUpdates.HANDLERS,
	...VASTUpdates.HANDLERS,
};

function setActiveCreativeId(state, action) {
	return {
		...state,
		activeCreativeId: action.payload.creativeId,
	};
}

function clearActiveCreativeId(state, action) {
	return {
		...state,
		activeCreativeId: null,
	};
}

function clearList(state, action) {
	return {
		...initialCreativesState,
		filterInfo: state.filterInfo,
		columnFilters:state.columnFilters,
	};
}

function isLoadingCreatives(state, action) {
	return {
		...state,
		isLoaded: false,
	};
}

function initCreatives(state, action) {
	let { data } = action.payload;
	return {
		...state,
		...data,
		isLoaded: true,
		hasErrorLoading: false,
	};
}

function initErrorCreatives(state, action) {
	return {
		...state,
		isLoaded: true,
		hasErrorLoading: true,
	};
}

function bulkStatusChangeRequest(state, action) {
	return {
		...state,
		isUpdating: true,
	};
}

function bulkStatusChangeSuccess(state, action) {
	let updatedCreatives = action.payload.data;
	let updatedCreativesById = {};

	updatedCreatives.forEach((newCreative) => {
		updatedCreativesById[newCreative.id] = newCreative;
	});

	let newItems = state.items.map((oldCreative) => {
		if (updatedCreativesById[oldCreative.id]) {
			return updatedCreativesById[oldCreative.id];
		}
		return oldCreative;
	});

	return {
		...state,
		items: newItems,
		isUpdating: false,
	};
}

function bulkStatusChangeError(state, action) {
	return {
		...state,
		isUpdating: false,
	};
}

function statusChanged(state, action) {
	return {
		...state,
		modalProps:{
			...state.modalProps,
			disabled:true,
			isLoaded:true,
		},
	};
}

function successStatusChange(state, action) {
	let { data }=action.payload
		, newItems=[].concat(state.items)
		;
	data.forEach((v) => {
		let itemId=state.items.findIndex(aa => aa.id===v.id);
		newItems.splice(itemId, 1, v);
	});
	return {
		...state,
		items: newItems,
		modalProps:{
			...state.modalProps,
			disabled:true,
			isLoaded:false,
		},
	};
}

function errorStatusChange(state, action) {
	return {
		...state,
		modalProps:{
			...state.modalProps,
			disabled:false,
			isLoaded:false,
			error:true,
		},
	};
}

function updateCreativeRequest(state, action) {
	return {
		...state,
		isUpdating: true,
	};
}

function updateCreativeSuccess(state, action) {
	let updatedCreative = action.payload.data
	 	, { items } = state
		, newItems = [].concat(items)
		, index = newItems.findIndex(creative => creative.id === updatedCreative.id)
		;
	if (index > -1) {
		newItems[index] = updatedCreative;
	}
	return {
		...state,
		items: newItems,
		isUpdating: false,
	};
}

function updateCreativeError(state, action) {
	return {
		...state,
		isUpdating: false,
	};
}

function deleteCreativesRequest(state, action) {
	return {
		...state,
		isDeleting: true,
	};
}

function deleteCreativesSuccess(state, action) {
	let deletedIds = new Set(action?.payload?.data?.deleted || []);

	return {
		...state,
		items: state.items.filter(creative => !deletedIds.has(creative.id)),
		isDeleting: false,
		error: false,
	};
}

function deleteCreativesError(state, action) {
	return {
		...state,
		isDeleting: false,
		error: true,
	};
}

function duplicateCreativesRequest(state, action) {
	return {
		...state,
		isUpdating: true,
	};
}

function duplicateCreativesSuccess(state, action) {
	return {
		...state,
		isUpdating: false,
		hasErrorLoading: false,
	};
}

function duplicateCreativesError(state, action) {
	return {
		...state,
		isUpdating: false,
		hasErrorLoading: true,
	};
}

function getCreativesByConcept(state, action) {
	return {
		...state,
		modalProps:{
			...state.modalProps,
			conceptCreatives:{
				 isLoaded:false,
				 },
		 },
	};
}

function successCreativesByConcept(state, action) {
	const { items } = action.payload.data;
	const canDelete = canDeleteConcept(items);

	return {
		...state,
		modalProps: {
			...state.modalProps,
			conceptCreatives: {
				items,
				canDelete,
				isLoaded: true,
				hasErrorLoading: false,
			},
		},
	};
}

function errorCreativesByConcept(state, action) {
	return {
		...state,
		modalProps:{
			...state.modalProps,
			conceptCreatives:{
					  isLoaded: false
				, hasErrorLoading: true,
			},
		},
	};
}

function updateRow(state, action){
	let { data = {} } = action.payload || {};
	let newItems = mergeItem(state.items, data);

	return {
		...state,
		items: newItems,
	};
}

let reducerConfig = {
	typeRoot: "CREATIVES",
	actionTypes: CREATIVES_ACTIONS,
	actionCreators: {
		clearCreatives,
		loadCreatives,
		fetchCreativesById,
		duplicateSingleCreative,
		duplicateCreativesBulk,
		statusChange,
		renameCreative,
		renameCreativeFilename,
		deleteCreatives,
		submitCreativesToQA,
		loadCreativeDistincts,
		downloadCreative,
		downloadCreativeSummary,
		fetchFilenameConflicts,
		uploadCreative,
		addSocketCreative,
		updateApprovalsFromSocket,
		updateCreativeVersionCountsFromSocket,
		setActiveCreative,
		clearActiveCreative,
		importCreativeLabels,
	},
	initialState: initialCreativesState,
	handlers,
};

reducerConfig = addFiltering(reducerConfig, { sort: "lastModified:DESC" });
reducerConfig = addColumnFilters(reducerConfig, {
	conceptName:{
		name:"conceptName",
		type:"String",
		displayName:"Concept Name",
	},
	numberOfVersions:{
		name:"numberOfVersions",
		type:"Range",
		displayName:"Versions",
	},
	decisioningType:{
		name:"decisioningType",
		type:"Checkbox",
		displayName:"Decisioning",
	},
	adType:{
		name:"adType",
		type:"Checkbox",
		displayName:"Type",
		dictionaryName: "adType",
	},
	placementSize:{
		name:"placementSize",
		type:"Checkbox",
		displayName:"Placement Size",
		filterParser(name, params){
			let width = []
				, height =[]
				;
			params.forEach((v) => {
				let dims = v.split("x");

				width.push(parseInt(dims[0]));
				height.push(parseInt(dims[1]));
			});

			return `in(width, ${width}), in(height, ${height})`;
		},
	},
	approval:{
		name:"approval",
		type:"Checkbox",
		displayName:"Status",
		dictionaryName: "approval",
	},
	expandedSize:{
		name:"expandedSize",
		type:"Checkbox",
		displayName:"Expand Size",
		filterParser(name, params){
			let width = []
				, height =[]
				;
			params.forEach((v) => {
				let dims = v.split("x");

				width.push(parseInt(dims[0]));
				height.push(parseInt(dims[1]));
			});

			return `in(expandedWidth, ${width}), in(expandedHeight, ${height})`;
		},
	},
	isVideo:{
		name:"isVideo",
		type:"Checkbox",
		displayName:"Video",
		dictionaryName: "boolean",
	},
	isRichload:{
		name:"isRichload",
		type:"Checkbox",
		displayName:"Richloads",
		dictionaryName: "boolean",
	},
	totalFileSize:{
		name:"totalFileSize",
		type:"Range",
		displayName:"Total File Size",
	},
	labelsCnt:{
		name:"labelsCnt",
		type:"Range",
		displayName:"Creative Labels",
	},
	autoClassificationStatus:{
		name: "autoClassificationStatus",
		type: "Checkbox",
		displayName: "Auto-classification Status",
	},
	asComponentVersion:{
		name:"asComponentVersion",
		type:"Checkbox",
		displayName:"API Version",
	},
	filesize:{
		name:"filesize",
		type:"Range",
		displayName:"Base File Size",
	},
	numClickTags:{
		name:"numClickTags",
		type:"Checkbox",
		displayName:"# ClickTags",
		dictionaryName: "numClicktags",
	},
	filename:{
		name:"filename",
		type:"String",
		displayName:"File Name",
	},
	lastModified:{
		name:"lastModified",
		type:"Date",
		displayName:"Last Modified",
	},
});

const { updateColumnFilter } = reducerConfig.actionCreators;

export function loadCreativeDistincts(libraryId, columnNameList){
	/*
		Fetches distinct values for the provided creativelibraries column names,
		and loads them as options for the corresponding columnFilters,
		i.e. for Checkbox filters.
	*/
	let commaSeparatedColumnNames = columnNameList.join(",");

	return (dispatch, getState) => {
		return dispatch({
			type: CREATIVES_ACTIONS.LOAD_CREATIVES_DISTINCTS_AJAX,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/distincts`,
				params: { distincts: commaSeparatedColumnNames },
				success: CREATIVES_ACTIONS.LOAD_CREATIVES_DISTINCTS_SUCCESS,
				error: CREATIVES_ACTIONS.LOAD_CREATIVES_DISTINCTS_ERROR,
			},
		}).then((response) => {
			if(response.success){
				let { data = {} } = response.payload || {};
				let keys = Object.keys(data);

				keys.forEach((key) => {
					let distincts = data[key];
					/*
						The server response usually contains a null value, and an empty string,
						which we're not interested in.

						We can trim them out here, rather than have to add logic to ignore them
						every time we want to work with these values.
						Note we explicity look for null/empty strings. Checking for false will exclude
						valid falsy values
					*/
					let filteredDistincts = distincts.filter((item) => {
						return item !== null && item !== '';
					});

					// Patch the options into the corresponding columnFilters.
					dispatch(updateColumnFilter(key, {
						options: filteredDistincts,
					}));
				});
			}
		});
	};
}

export function duplicateSingleCreative(values) {
	const { copyVersions, creativeId, libraryId, newCreativeName } = values;

	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.DUPLICATE_CREATIVES_AJAX,
			AJAX: {
				url: `creativelibraries/${libraryId}/creatives/duplicate`,
				params: { copyVersions: copyVersions ? 1 : 0 },
				method: 'post',
				data: {
					id: creativeId,
					newName: newCreativeName,
				},
				success: CREATIVES_ACTIONS.DUPLICATE_CREATIVES_SUCCESS,
				error: CREATIVES_ACTIONS.DUPLICATE_CREATIVES_ERROR,
			},
		});
	};
}

function handleBulkDuplicationResponse(response, dispatch) {
	let errorMessages = [];
	let successMessages = [];

	//response is list of creatives, ones that didn't copy will have an `error` property
	response.payload.data.forEach((creative) => {
		if (creative.error) {
			errorMessages.push(`[${creative.id}] ${creative.error}`);
		} else {
			successMessages.push(`[${creative.id}] ${creative.name} duplicated successfully`);
		}
	});

	if (errorMessages.length) {
		//if there are any errors, show the error growl
		dispatch(addNotification({
			type: "error",
			id: Math.random(),
			messageHeader: "Error!",
			message: 'Some creatives not duplicated',
		}));
	} else {
		//if there are only successes, show the success growl
		dispatch(addNotification({
			type: "success",
			id: Math.random(),
			messageHeader: "Success!",
			message: `${successMessages.length} creatives duplicated successfully`,
		}));
	}
	return Promise.resolve({ errorMessages, successMessages });
}

export function duplicateCreativesBulk(values) {
	const { creatives, libraryId, copyVersions } = values;

	// format: [{id: 123, newName: "new01"}, {id: 456, newName: "new02"}]
	let creativeData = creatives.map(creative => ({
		id: creative.id,
		newName: creative.newName,
	}));

	return (dispatch) => {
		return dispatch({
			type: CREATIVES_ACTIONS.DUPLICATE_CREATIVES_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/creatives/duplicate-many`,
				params: { copyVersions: copyVersions ? 1 : 0 },
				method: 'post',
				data: {
					creatives: creativeData,
				},
				success: CREATIVES_ACTIONS.DUPLICATE_CREATIVES_SUCCESS,
				error: CREATIVES_ACTIONS.DUPLICATE_CREATIVES_ERROR,
			},
			meta: {
				//custom growls because api always returns success, even when it fails
				suppressGrowl: true,
			},
		}).then(response => handleBulkDuplicationResponse(response, dispatch));
	};
}

function updateLabelCount(state, action){
	const payloadCreatives = action.payload?.data?.creatives || [];
	const items = payloadCreatives.reduce((prev, creative) => {
		let existingCreative = prev.find(item => `${item.id}` === `${creative.id}`);
		if(existingCreative){
			existingCreative.labelsCnt = creative.labels.length;
		}
		return prev;
	}, [...state.items]);

	const newState = {
		...state,
		items,
	};

	return newState;
}

function subtractLabels(state, action){
	const deletedIds = action.payload?.data?.deleted || [];
	const { creativeId } = action.payload.original || {};

	return {
		...state,
		items: state.items.map((creative) => {
			if(`${creative.id}` === `${creativeId}`){
				creative.labelsCnt = creative.labelsCnt - deletedIds.length;
			}
			return creative;
		}),
	};
}

export default createReducer(reducerConfig.initialState, reducerConfig.handlers);
export const Creatives = reducerConfig.actionCreators;

export const PRIVATE = {
	updateCreativesApproval,
};
